import { nanoid } from "nanoid/non-secure";
import { z } from "zod";

import type {
	GetWorkflowTemplateResponseJSON,
	SaveWorkflowTemplatePayload,
	WorkflowTemplateSignatureMapJSON,
	WorkflowTemplateUserMapJSON,
} from "../../../api/services/defs/workflow-templates";

/**
 * Client limits mirror the legacy page's `Config` (250-char names, 50 to-dos) and the
 * marshmallow schema's floors (at least one to-do, days >= 1) so nothing valid here can
 * 422 server-side. The server's hard name ceiling is 280 (`MAX_WORKFLOW_TEXT_LENGTH`);
 * 250 is kept as the stricter legacy client cap.
 */
export const NAME_MAX_LENGTH = 250;
export const TODOS_MAX_COUNT = 50;

/** The three wildcard assignees the server accepts in place of a user id. */
export const ASSIGNEE_WILDCARDS = [
	{ id: "self", name: "Employee", avatarInitials: "E" },
	{ id: "manager", name: "Employee's Manager", avatarInitials: "EM" },
	{ id: "other", name: "Other Employee", avatarInitials: "OE" },
] as const;

export type AssigneeWildcard = (typeof ASSIGNEE_WILDCARDS)[number];

export const getAssigneeWildcard = (id: string): AssigneeWildcard | undefined =>
	ASSIGNEE_WILDCARDS.find((wildcard) => wildcard.id === id);

const AssigneeSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const SignatureTemplateRefSchema = z.object({
	id: z.string(),
	name: z.string(),
	signerCount: z.number().optional(),
});

export const TodoRowSchema = z
	.object({
		/** Client-only row key for drag-and-drop; stripped from the save payload. */
		_id: z.string(),
		name: z
			.string()
			.trim()
			.min(1, "This is a required field")
			.max(NAME_MAX_LENGTH, `To-do name must be ${NAME_MAX_LENGTH} characters or less`),
		assignee: AssigneeSchema.nullable(),
		daysToComplete: z
			.number({ error: "This is a required field" })
			.int("Days to complete must be a whole number")
			.min(1, "Days to complete must be at least 1")
			.nullable()
			.refine((value): value is number => value !== null, "This is a required field"),
		signatureTemplate: SignatureTemplateRefSchema.nullable(),
	})
	.superRefine((todo, ctx) => {
		// The signing wizard's template defines who signs, so an attached signature
		// request replaces the assignee — mirrors the marshmallow cross-field rule.
		if (!todo.signatureTemplate && !todo.assignee) {
			ctx.addIssue({
				code: "custom",
				message: "This is a required field",
				path: ["assignee"],
			});
		}
	});

export type TodoRow = z.infer<typeof TodoRowSchema>;

export const TemplateFormSchema = z.object({
	todos: z
		.array(TodoRowSchema)
		.min(1, "Add at least one to-do to save this template.")
		.max(TODOS_MAX_COUNT, `Cannot have more than ${TODOS_MAX_COUNT} to-dos`),
});

export type TemplateFormValues = z.infer<typeof TemplateFormSchema>;

export const buildEmptyTodoRow = (): TodoRow => ({
	_id: nanoid(),
	name: "",
	assignee: null,
	daysToComplete: null,
	signatureTemplate: null,
});

/**
 * Map a fetched template onto form rows, resolving actor ids through the wildcards and
 * `display_user_map`, and signature template ids through `signature_request_map`.
 * Deactivated users are kept selected (the server names them in its save error), and ids
 * the maps no longer know still render by id rather than dropping the row's data.
 */
export const toTodoRows = (
	todos: GetWorkflowTemplateResponseJSON["template"]["todos"],
	userMap: WorkflowTemplateUserMapJSON,
	signatureMap: WorkflowTemplateSignatureMapJSON
): TodoRow[] =>
	todos.map((todo) => {
		const signatureTemplateId =
			typeof todo.documentSigning === "object" && todo.documentSigning !== null
				? todo.documentSigning.id
				: todo.documentSigning;

		let assignee: TodoRow["assignee"] = null;
		if (todo.actor) {
			const wildcard = getAssigneeWildcard(todo.actor);
			assignee = wildcard
				? { id: wildcard.id, name: wildcard.name }
				: { id: todo.actor, name: userMap[todo.actor]?.name ?? "Unknown employee" };
		}

		return {
			_id: nanoid(),
			name: todo.label ?? "",
			assignee,
			daysToComplete: typeof todo.daysToComplete === "number" ? todo.daysToComplete : null,
			signatureTemplate: signatureTemplateId
				? {
						id: signatureTemplateId,
						name: signatureMap[signatureTemplateId]?.name ?? "Signature Template",
						signerCount: signatureMap[signatureTemplateId]?.signers.length,
					}
				: null,
		};
	});

/** Build the legacy save payload; the schema guarantees `daysToComplete` is set by now. */
export const toSavePayload = (name: string, todos: TodoRow[]): SaveWorkflowTemplatePayload => ({
	name,
	todos: todos.map((todo) => ({
		label: todo.name.trim(),
		actor: todo.signatureTemplate ? null : (todo.assignee?.id ?? null),
		daysToComplete: todo.daysToComplete ?? 0,
		documentSigning: todo.signatureTemplate ? { id: todo.signatureTemplate.id } : null,
	})),
});
